import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Global tooltip manager to ensure only one tooltip is visible at a time
const tooltipInstances = new Map()

const registerTooltip = (id, hideFunction) => {
  tooltipInstances.set(id, hideFunction)
}

const unregisterTooltip = (id) => {
  tooltipInstances.delete(id)
}

const hideAllOtherTooltips = (currentId) => {
  tooltipInstances.forEach((hideFunction, id) => {
    if (id !== currentId) {
      hideFunction()
    }
  })
}

export default function BubbleTooltip({ button, content, direction = 'bottom' }) {
  const [tooltipPosition, setTooltipPosition] = useState('center')
  const [tooltipVertical, setTooltipVertical] = useState(direction)
  const [tooltipStyle, setTooltipStyle] = useState({})
  const [isVisible, setIsVisible] = useState(false)
  const [isPositioned, setIsPositioned] = useState(false)
  const [tooltipWidth, setTooltipWidth] = useState('auto')
  const [shouldShow, setShouldShow] = useState(false)
  const triggerRef = useRef(null)
  const tooltipRef = useRef(null)
  const hideTimeoutRef = useRef(null)
  const tooltipIdRef = useRef(Math.random().toString(36).substr(2, 9))

  // Hide function for this tooltip instance
  const hideTooltip = () => {
    setIsVisible(false)
    setShouldShow(false)
    setIsPositioned(false)
  }

  // Register this tooltip instance
  useEffect(() => {
    const tooltipId = tooltipIdRef.current
    registerTooltip(tooltipId, hideTooltip)
    return () => {
      unregisterTooltip(tooltipId)
    }
  }, [])

  // Calculate optimal width based on text content with 2:1 width/height ratio
  const calculateOptimalWidth = (content) => {
    // Handle both string and React element content
    let text = ''
    if (typeof content === 'string') {
      text = content
    } else if (content && typeof content === 'object') {
      // For React elements, extract text content
      text = content.props?.children?.toString() || ''
    }
    
    // Estimate character width (12px font size, roughly 7px per character)
    const charWidth = 7
    const padding = 24 // 12px padding on each side
    const minWidth = 200 // Increased minimum width for wider tooltips
    const maxWidth = 500 // Increased maximum width for wider tooltips
    
    // Estimate line height (18px line height + padding)
    const lineHeight = 18
    const verticalPadding = 16 // 8px padding top and bottom
    
    // Calculate estimated height based on text
    const estimatedLines = Math.ceil(text.length / 50) // More conservative: ~50 chars per line
    const estimatedHeight = (estimatedLines * lineHeight) + verticalPadding
    
    // Target 3:1 width/height ratio - make width 3x the height
    const targetWidth = estimatedHeight * 3
    
    // Calculate minimum width needed for text
    const words = text.split(' ')
    let minTextWidth = text.length * charWidth + padding
    
    if (words.length > 3) {
      // For longer text, calculate minimum width needed
      const avgWordsPerLine = Math.ceil(words.length / 2)
      const longestLine = Math.max(...words.map(word => word.length))
      const lineWidth = Math.max(longestLine * charWidth, avgWordsPerLine * charWidth * 1.2)
      minTextWidth = lineWidth + padding
    }
    
    // Use the larger of minimum text width or target width, but respect maxWidth
    const optimalWidth = Math.max(
      minWidth, 
      Math.min(maxWidth, Math.max(minTextWidth, targetWidth))
    )
    
    return optimalWidth
  }

  useEffect(() => {
    const checkPosition = () => {
      if (triggerRef.current && tooltipRef.current) {
        // Find the actual button element within the wrapper
        const buttonElement = triggerRef.current.querySelector('button')
        const triggerRect = buttonElement ? buttonElement.getBoundingClientRect() : triggerRef.current.getBoundingClientRect()
        const tooltipRect = tooltipRef.current.getBoundingClientRect()
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        
        // Determine position based on direction prop or trigger location
        let position = 'center'
        let vertical = direction
        
        if (direction === 'right') {
          position = 'right'
          vertical = 'center'
        } else if (direction === 'left') {
          position = 'left'
          vertical = 'center'
        } else if (direction === 'top' || direction === 'bottom') {
          // Respect explicit vertical direction; auto horizontal
          const triggerCenter = triggerRect.left + (triggerRect.width / 2)
          const screenQuarter = viewportWidth * 0.25
          const screenHalf = viewportWidth * 0.5
          if (triggerCenter < screenQuarter) {
            position = 'right'
          } else if (triggerCenter > screenHalf + screenQuarter) {
            position = 'left'
          } else {
            position = 'center'
          }
          vertical = direction
        } else {
          // Auto-positioning for top/bottom
          const triggerCenter = triggerRect.left + (triggerRect.width / 2)
          const screenQuarter = viewportWidth * 0.25
          const screenHalf = viewportWidth * 0.5
          
          if (triggerCenter < screenQuarter) {
            position = 'right'
          } else if (triggerCenter > screenHalf + screenQuarter) {
            position = 'left'
          } else {
            position = 'center'
          }
          
          // Determine vertical position based on available space
          const tooltipHeight = 200 // Approximate tooltip height
          
          const spaceBelow = viewportHeight - triggerRect.bottom
          const spaceAbove = triggerRect.top
          
          const showAbove = spaceBelow < tooltipHeight && spaceAbove > tooltipHeight
          vertical = showAbove ? 'top' : 'bottom'
        }
        
        // Calculate absolute positioning
        let left, top
        
        if (position === 'center') {
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2)
        } else if (position === 'left') {
          left = triggerRect.left - tooltipRect.width - 8
        } else if (position === 'right') {
          left = triggerRect.right + 8
        }
        
        if (vertical === 'top') {
          top = triggerRect.top - tooltipRect.height - 4
        } else if (vertical === 'bottom') {
          top = triggerRect.bottom + 4
        } else if (vertical === 'center') {
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2)
        }
        
        
        setTooltipStyle({
          position: 'fixed',
          left: `${left}px`,
          top: `${top}px`,
          zIndex: 9999,
          width: `${tooltipWidth}px`
        })
        
        setTooltipPosition(position)
        setTooltipVertical(vertical)
        setIsPositioned(true)
      }
    }

    if (isVisible) {
      // Use requestAnimationFrame to ensure the tooltip is rendered before positioning
      requestAnimationFrame(() => {
        checkPosition()
      })
      window.addEventListener('resize', checkPosition)
      window.addEventListener('scroll', checkPosition)
    }
    
    return () => {
      window.removeEventListener('resize', checkPosition)
      window.removeEventListener('scroll', checkPosition)
      // Clean up timeout on unmount
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [isVisible, tooltipWidth, direction])

  const getTooltipClasses = () => {
    return "px-3 py-2 bg-[#101828] text-white text-xs font-semibold leading-[18px] rounded-md transition-opacity duration-200 [&_a]:cursor-pointer [&_a]:underline [&_a:hover]:no-underline"
  }

  const getArrowClasses = () => {
    const baseClasses = "absolute w-0 h-0"
    
    // If placed horizontally (left/right) and vertically centered, show horizontal arrow
    if (tooltipVertical === 'center' && (tooltipPosition === 'left' || tooltipPosition === 'right')) {
      if (tooltipPosition === 'right') {
        return `${baseClasses} left-0 top-1/2 transform -translate-y-1/2 -translate-x-1 border-r-4 border-r-[#101828] border-t-4 border-b-4 border-t-transparent border-b-transparent`
      }
      // left
      return `${baseClasses} right-0 top-1/2 transform -translate-y-1/2 translate-x-1 border-l-4 border-l-[#101828] border-t-4 border-b-4 border-t-transparent border-b-transparent`
    }
    
    // Otherwise, show vertical arrow for top/bottom
    const verticalArrowClasses = tooltipVertical === 'top'
      ? "top-full border-t-4 border-t-[#101828]"
      : "bottom-full border-b-4 border-b-[#101828]"
    
    // Horizontal arrow positioning
    let horizontalArrowClasses = ""
    switch (tooltipPosition) {
      case 'left':
        horizontalArrowClasses = "right-4 transform translate-x-0"
        break
      case 'right':
        horizontalArrowClasses = "left-4 transform translate-x-0"
        break
      default:
        horizontalArrowClasses = "left-1/2 transform -translate-x-1/2"
    }
    
    return `${baseClasses} border-l-4 border-r-4 border-transparent ${verticalArrowClasses} ${horizontalArrowClasses}`
  }

  const tooltipElement = shouldShow ? (
    <div 
      ref={tooltipRef}
      className={getTooltipClasses()}
      style={{
        ...(isPositioned ? tooltipStyle : { 
          position: 'fixed', 
          left: '-9999px', 
          top: '-9999px', 
          zIndex: 9999,
          width: `${tooltipWidth}px`
        }),
        pointerEvents: 'auto', // Ensure mouse events work
        cursor: 'default' // Default cursor for tooltip
      }}
      onMouseEnter={() => {
        // Hide all other tooltips first
        hideAllOtherTooltips(tooltipIdRef.current)
        // Clear any existing timeout
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current)
          hideTimeoutRef.current = null
        }
        setIsVisible(true)
        setShouldShow(true)
      }}
      onMouseLeave={() => {
        // Start timeout to hide tooltip
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false)
          setShouldShow(false)
          setIsPositioned(false)
        }, 250)
      }}
    >
      {content}
      <div className={getArrowClasses()} style={{ pointerEvents: 'none' }}></div>
    </div>
  ) : null

  return (
    <>
      <div 
        ref={triggerRef}
        onMouseEnter={() => {
          // Hide all other tooltips first
          hideAllOtherTooltips(tooltipIdRef.current)
          // Clear any existing timeout
          if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current)
            hideTimeoutRef.current = null
          }
          const optimalWidth = calculateOptimalWidth(content)
          setTooltipWidth(optimalWidth)
          setIsVisible(true)
          setShouldShow(true)
        }}
        onMouseLeave={() => {
          // Start timeout to hide tooltip
          hideTimeoutRef.current = setTimeout(() => {
            setIsVisible(false)
            setShouldShow(false)
            setIsPositioned(false)
          }, 250)
        }}
        className="flex flex-1"
      >
        {button}
      </div>
      {tooltipElement && createPortal(tooltipElement, document.body)}
    </>
  )
}

BubbleTooltip.propTypes = {
  button: PropTypes.element.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  direction: PropTypes.string,
}
